import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Star, MessageSquarePlus, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { CommunityPost, CarExperience } from '@/types/community';

interface Props {
  variantId: string;
  modelId: string;
  brandSlug: string;
  modelSlug: string;
  modelName: string;
  brandName: string;
  brandId?: string;
}

export function VariantCommunitySection({ variantId, modelId, brandSlug, modelSlug, modelName, brandName, brandId }: Props) {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [experiences, setExperiences] = useState<CarExperience[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const [{ data: p }, { data: e }] = await Promise.all([
        supabase.from('community_posts')
          .select('*')
          .eq('variant_id', variantId)
          .eq('is_hidden', false)
          .order('created_at', { ascending: false })
          .limit(3),
        supabase.from('car_experiences')
          .select('*')
          .eq('variant_id', variantId)
          .eq('status', 'approved')
          .eq('is_hidden', false)
          .order('created_at', { ascending: false })
          .limit(3),
      ]);
      if (!alive) return;
      setPosts((p as any) || []);
      setExperiences((e as any) || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [variantId]);

  const askUrl = `/community/sporgsmaal/nyt?model=${modelId}&variant=${variantId}${brandId ? `&brand=${brandId}` : ''}`;
  const shareUrl = `/community/erfaringer/ny?model=${modelId}&variant=${variantId}${brandId ? `&brand=${brandId}` : ''}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-accent" />
          Fællesskab om denne variant
        </CardTitle>
        <Link
          to={`/community/model/${brandSlug}/${modelSlug}${experiences.length > 0 && posts.length === 0 ? '?tab=erfaringer' : ''}`}
          className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          Se alle <ArrowRight className="w-3 h-3" />
        </Link>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            {[0, 1].map(i => <div key={i} className="h-12 bg-secondary/50 rounded animate-pulse" />)}
          </div>
        ) : (posts.length === 0 && experiences.length === 0) ? (
          <p className="text-sm text-muted-foreground">
            Ingen indlæg om <strong>{brandName} {modelName}</strong> endnu. Vær den første.
          </p>
        ) : (
          <>
            {posts.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Seneste spørgsmål</div>
                <ul className="space-y-1.5">
                  {posts.map(p => (
                    <li key={p.id}>
                      <Link to={`/community/sporgsmaal/${p.id}/${p.slug}`} className="block p-2 -mx-2 rounded-md hover:bg-secondary/60">
                        <div className="text-sm font-medium text-foreground line-clamp-1">{p.title}</div>
                        <div className="text-xs text-muted-foreground">{p.vote_score} stemmer · {p.status === 'solved' ? 'Løst' : p.status === 'answered' ? 'Besvaret' : 'Åbent'}</div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {experiences.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Erfaringer fra ejere</div>
                  {(() => {
                    const valid = experiences.filter(e => !(e as any).low_trust);
                    if (valid.length === 0) return null;
                    const avg = valid.reduce((s, e) => s + (e.rating_overall || 0), 0) / valid.length;
                    return (
                      <div className="flex items-center gap-1 text-xs text-amber-600" title={`Gennemsnit af ${valid.length} ${valid.length === 1 ? 'erfaring' : 'erfaringer'} (kort ejertid tæller ikke)`}>
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span className="font-medium">{avg.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                        <span className="text-muted-foreground">af 5 ({valid.length})</span>
                      </div>
                    );
                  })()}
                </div>
                <ul className="space-y-1.5">
                  {experiences.map(e => (
                    <li key={e.id}>
                      <Link
                        to={`/community/model/${brandSlug}/${modelSlug}?tab=erfaringer#exp-${e.id}`}
                        className="block p-2 -mx-2 rounded-md hover:bg-secondary/60"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-foreground line-clamp-1 flex-1">{e.body || e.pros?.[0] || 'Erfaring delt af ejer'}</div>
                          <div className="flex items-center gap-0.5 text-amber-500 flex-shrink-0">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} className={`w-3 h-3 ${i < e.rating_overall ? 'fill-current' : ''}`} />
                            ))}
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        <div className="flex flex-col gap-2 pt-2">
          <Link to={askUrl}>
            <Button variant="accent" size="sm" className="w-full gap-2">
              <MessageSquarePlus className="w-4 h-4" /> Stil spørgsmål om denne variant
            </Button>
          </Link>
          <Link to={shareUrl}>
            <Button variant="outline" size="sm" className="w-full gap-2">
              <Star className="w-4 h-4" /> Del din erfaring
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
